import {isCompletionChunk, type LogEntry} from '../../../types/session'
import {useLogs} from '../../../hooks/useLogs'
import {useCurrentUser} from '../../../contexts/CurrentUserContext'
import {CopilotMessageBubble, CopilotMessageContainer, CopilotMessageLoading} from './CopilotMessageBubble'
import {ResolvedPlanStatus} from '../../session-logs/ResolvedPlanStatus'
import {hasPrMetadataTags} from '../../../utils/pr-metadata-tags'
import {extractPlanContent, stripPlanTags} from '../../../utils/plan-tags'
import {getCustomAgentId} from '../../../utils/group-agent-logs'
import {useEffect} from 'react'
import {DismissedPlanDisplay} from '../../session-logs/DismissedPlanDisplay'
import type {PlanState} from '../../../hooks/use-patch-session'

/** CCA-specific actions for end-of-session plan approval */
export const CCA_PLAN_ACTION_IMPLEMENT = 'implement'
export const CCA_PLAN_ACTION_EXIT = 'exit_only'
export const CCA_PLAN_ACTIONS = [CCA_PLAN_ACTION_IMPLEMENT, CCA_PLAN_ACTION_EXIT]

interface CopilotFinalStopMessageProps {
  logsPollingInterval: number
  /** Called when the user approves the plan — parent should kick off a new session */
  onApprove?: (action: string, planSummary: string) => void
  /** Plan state: undefined = pending, approved = resolved chip, dismissed = read-only bubble */
  planState?: PlanState
  /**
   * Called with the plan content string when an actionable plan is detected,
   * or null when no actionable plan is present. The parent uses this to render
   * the plan approval UI in the bottom slot (replacing the chat input).
   */
  onPlanDetected?: (planContent: string | null) => void
}

export function CopilotFinalStopMessage({
  logsPollingInterval,
  onApprove,
  planState,
  onPlanDetected,
}: CopilotFinalStopMessageProps) {
  const {currentUserIsSessionOwner} = useCurrentUser()
  const {
    data: logsData,
    isLoading: logsIsLoading,
    error: logsError,
  } = useLogs({logsPollingInterval, currentUserIsSessionOwner})

  const stopMessages = extractStopMessages(logsData)
  const combinedContent = stopMessages.map(m => m.content).join('\n\n')
  const {planContent, remainingContent} = extractPlanContent(combinedContent)

  const hasPlanContent = !!planContent
  const isActionablePlan = hasPlanContent && !!onApprove && !planState

  useEffect(() => {
    onPlanDetected?.(isActionablePlan ? planContent : null)
  }, [isActionablePlan, planContent, onPlanDetected])

  if (logsIsLoading) {
    return <CopilotMessageLoading />
  }

  if (logsError || stopMessages.length === 0) {
    return null
  }

  // No plan tags — render everything as regular markdown
  if (!planContent) {
    return (
      <CopilotMessageContainer>
        <CopilotMessageBubble markdown={stripPlanTags(combinedContent)} />
      </CopilotMessageContainer>
    )
  }

  // Dismissed plan — render read-only with border
  if (planState === 'dismissed') {
    return <DismissedPlanDisplay planContent={planContent} />
  }

  if (planState === 'approved') {
    return <ResolvedPlanStatus />
  }

  // Only render the remaining (non-plan) content here.
  // The PlanApprovalDisplay is rendered by the parent in the bottom slot.
  return remainingContent ? (
    <CopilotMessageContainer>
      <CopilotMessageBubble markdown={remainingContent} />
    </CopilotMessageContainer>
  ) : null
}

/**
 * Extracts trailing assistant stop messages from log entries, excluding PR metadata.
 * Only messages at the end of the log (after all tool calls) are extracted,
 * matching the filtering behavior in preprocessLogEntries.
 */
function extractStopMessages(logsData: LogEntry[] = []): Array<{id: string; content: string}> {
  const messages: Array<{id: string; content: string}> = []

  // Walk backward from the end to find trailing stop entries
  for (let i = logsData.length - 1; i >= 0; i--) {
    const logEntry = logsData[i]
    if (!logEntry) break

    // Skip non-CompletionChunk entries (e.g. lifecycle events like session.idle)
    if (!isCompletionChunk(logEntry)) continue

    // Skip subagent entries
    if (getCustomAgentId(logEntry)) break

    const choice = logEntry.choices[0]
    const hasToolCalls = choice?.delta.tool_calls && choice.delta.tool_calls.length > 0

    // Skip empty stop chunks (no content, no tool calls)
    if (choice?.finish_reason === 'stop' && !choice.delta.content && !hasToolCalls) {
      continue
    }

    if (
      choice?.finish_reason === 'stop' &&
      choice.delta.role === 'assistant' &&
      choice.delta.content &&
      !hasToolCalls &&
      // Exclude PR description entries - we want the summary, not the PR metadata
      !hasPrMetadataTags(choice.delta.content)
    ) {
      const content = choice.delta.content.trim()
      if (content) {
        messages.unshift({id: logEntry.id, content})
      }
      continue
    }

    // Hit a non-stop entry — stop
    break
  }

  return messages
}
